import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getMySubscriptions, manageMySubscription } from '../../api/subscription';
import { createCheckoutSession, getPackages } from '../../api/payment';
import useCheckoutConversion from '../../utils/hooks/useCheckoutConversion';
import LoadingSpinner from '../../components/LoadingSpinner';
import {
  Banner,
  Card,
  CardButton,
  CardMeta,
  CardStatus,
  CardTitle,
  EmptyText,
  ErrorText,
  Grid,
  Main,
  Section,
  SectionTitle,
  Subtitle,
  Title,
  UpgradeCard,
  UpgradeCardFeature,
  UpgradeCardIcon,
  UpgradeCardLabel,
  UpgradeCardPrice,
} from './styles';

const formatDate = value => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString();
};

const SubscriptionManagement = () => {
  const location = useLocation();
  useCheckoutConversion(location.search);
  const query = new URLSearchParams(location.search);
  const showExpired = query.get('status') === 'expired';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [workingSubscriptionId, setWorkingSubscriptionId] = useState('');
  const [pricing, setPricing] = useState({ plans: [], propsAddon: null, topUpConfig: null });
  const [topUpCredits, setTopUpCredits] = useState('20');

  const access = useSelector(state => state.access);

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getMySubscriptions();
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load subscriptions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  useEffect(() => {
    getPackages()
      .then(data => {
        if (data?.plans || data?.topUpConfig) {
          setPricing({
            plans: Array.isArray(data?.plans) ? data.plans : [],
            propsAddon: data?.propsAddon || null,
            topUpConfig: data?.topUpConfig || null
          });
        }
      })
      .catch(() => {
        setPricing({ plans: [], propsAddon: null, topUpConfig: null });
      });
  }, []);

  const currentSubscription = useMemo(
    () => subscriptions.find(sub => sub.kind === 'plan') || null,
    [subscriptions]
  );

  const propsSubscription = useMemo(
    () => subscriptions.find(sub => sub.kind === 'props_addon') || null,
    [subscriptions]
  );

  const propsSubscriptionActive = Boolean(
    propsSubscription && ['active', 'trialing'].includes(String(propsSubscription.status || '').toLowerCase())
  );

  const toggleCancel = async sub => {
    setWorkingSubscriptionId(sub.stripeSubscriptionId);
    setError('');
    try {
      await manageMySubscription(sub.stripeSubscriptionId, !sub.cancelAtPeriodEnd);
      await refresh();
    } catch (err) {
      setError(err.message || 'Failed to update subscription.');
    } finally {
      setWorkingSubscriptionId('');
    }
  };

  const startCheckout = async payload => {
    setError('');
    try {
      const session = await createCheckoutSession({ ...payload, returnPath: '/subscription-management' });
      if (session?.url) {
        window.location.assign(session.url);
      } else {
        setError('Checkout session did not return a URL.');
      }
    } catch (err) {
      setError(err.message || 'Failed to start checkout.');
    }
  };

  const startCustomTopUp = async () => {
    const increment = Number(pricing?.topUpConfig?.increment || 5);
    const minCredits = Number(pricing?.topUpConfig?.minCredits || 5);
    const parsedCredits = Number(topUpCredits);

    if (!Number.isInteger(parsedCredits) || parsedCredits < minCredits || parsedCredits % increment !== 0) {
      setError(`Top-ups must be a whole number in increments of ${increment}, minimum ${minCredits}.`);
      return;
    }

    await startCheckout({ purchaseType: 'topup', topUpCredits: parsedCredits });
  };

  return (
    <Main>
      <Helmet>
        <title>Subscription Management</title>
      </Helmet>

      <Title>Subscription Management</Title>
      <Subtitle>Manage your monthly plan, Props add-on, current credits, and one-time top-ups.</Subtitle>

      {showExpired && <Banner>Your plan is inactive. Choose a monthly plan below to restore access.</Banner>}

      {loading && <LoadingSpinner label="Loading subscription details..." />}
      {!loading && error && <ErrorText>{error}</ErrorText>}

      {!loading && (
        <Section>
          <SectionTitle>Current Plan</SectionTitle>
          {currentSubscription ? (
            <Card>
              <CardTitle>
                <i className="fas fa-coins" style={{ marginRight: 7, color: '#f0c876' }} />
                {currentSubscription.label || 'Monthly Credit Plan'}
              </CardTitle>
              <CardStatus>Status: {currentSubscription.status}</CardStatus>
              <CardMeta>Credits Per Month: {Number(currentSubscription.monthlyCredits || access.monthlyCredits || 0)}</CardMeta>
              <CardMeta>Credits Remaining: {Number(access.remainingCredits || 0)}</CardMeta>
              <CardMeta>Credits Used: {Number(access.creditsUsed || 0)}</CardMeta>
              <CardMeta>Period End: {formatDate(currentSubscription.currentPeriodEnd)}</CardMeta>
              <CardMeta>Auto-Renew: {currentSubscription.cancelAtPeriodEnd ? 'Off' : 'On'}</CardMeta>

              {(currentSubscription.status === 'active' || currentSubscription.status === 'trialing') && (
                <CardButton
                  onClick={() => toggleCancel(currentSubscription)}
                  disabled={workingSubscriptionId === currentSubscription.stripeSubscriptionId}
                  $danger={!currentSubscription.cancelAtPeriodEnd}
                >
                  {workingSubscriptionId === currentSubscription.stripeSubscriptionId
                    ? 'Updating...'
                    : currentSubscription.cancelAtPeriodEnd
                    ? <><i className="fas fa-rotate-right" style={{ marginRight: 5 }} />Resume Auto-Renew</>
                    : <><i className="fas fa-xmark" style={{ marginRight: 5 }} />Cancel At Period End</>}
                </CardButton>
              )}
            </Card>
          ) : (
            <EmptyText>No active monthly plan found yet.</EmptyText>
          )}
        </Section>
      )}

      <Section $spaced>
        <SectionTitle>Monthly Plans</SectionTitle>
        <Grid>
          {pricing.plans.map(plan => {
            const isActivePlan = access.planKey === plan.key;
            return (
              <UpgradeCard key={plan.key} $owned={isActivePlan}>
                <UpgradeCardIcon $owned={isActivePlan}>
                  <i className="fas fa-coins" />
                </UpgradeCardIcon>
                <UpgradeCardLabel>{plan.label}</UpgradeCardLabel>
                <UpgradeCardPrice>
                  {plan.monthlyPrice || 'Price unavailable'}
                  <span>/mo</span>
                </UpgradeCardPrice>
                <UpgradeCardFeature><i className="fas fa-check" />{`${Number(plan.monthlyCredits || 0)} credits every month`}</UpgradeCardFeature>
                <UpgradeCardFeature><i className="fas fa-check" />All sports included</UpgradeCardFeature>
                <UpgradeCardFeature><i className="fas fa-check" />Prediction unlock credits auto-refill monthly</UpgradeCardFeature>

                {isActivePlan ? (
                  <CardButton disabled $owned>
                    <i className="fas fa-check" />Active Plan
                  </CardButton>
                ) : (
                  <CardButton onClick={() => startCheckout({ purchaseType: 'plan', planKey: plan.key })}>
                    Switch To Plan
                  </CardButton>
                )}
              </UpgradeCard>
            );
          })}
        </Grid>
      </Section>

      <Section $spaced>
        <SectionTitle>Props Add-On</SectionTitle>
        <Grid>
          <UpgradeCard $owned={propsSubscriptionActive || access.hasPropsUpgrade}>
            <UpgradeCardIcon $owned={propsSubscriptionActive || access.hasPropsUpgrade}>
              <i className="fas fa-chart-line" />
            </UpgradeCardIcon>
            <UpgradeCardLabel>{pricing?.propsAddon?.label || 'Props Add-On'}</UpgradeCardLabel>
            <UpgradeCardPrice>
              {pricing?.propsAddon?.monthlyPrice || 'Price unavailable'}
              <span>/mo</span>
            </UpgradeCardPrice>
            <UpgradeCardFeature><i className="fas fa-check" />Unlock Props page access</UpgradeCardFeature>
            <UpgradeCardFeature><i className="fas fa-check" />Unlock Team Roster insights window</UpgradeCardFeature>
            <UpgradeCardFeature><i className="fas fa-check" />Cancel anytime (separate from plan)</UpgradeCardFeature>

            {(propsSubscription || access.hasPropsUpgrade) ? (
              <>
                <CardMeta>Status: {propsSubscription?.status || 'active'}</CardMeta>
                <CardMeta>Auto-Renew: {propsSubscription?.cancelAtPeriodEnd ? 'Off' : 'On'}</CardMeta>
                {propsSubscription?.currentPeriodEnd && (
                  <CardMeta>Period End: {formatDate(propsSubscription.currentPeriodEnd)}</CardMeta>
                )}
                {propsSubscription?.stripeSubscriptionId ? (
                  <CardButton
                    onClick={() => toggleCancel(propsSubscription)}
                    disabled={workingSubscriptionId === propsSubscription.stripeSubscriptionId}
                    $danger={!propsSubscription.cancelAtPeriodEnd}
                  >
                    {workingSubscriptionId === propsSubscription.stripeSubscriptionId
                      ? 'Updating...'
                      : propsSubscription.cancelAtPeriodEnd
                      ? <><i className="fas fa-rotate-right" style={{ marginRight: 5 }} />Resume Auto-Renew</>
                      : <><i className="fas fa-xmark" style={{ marginRight: 5 }} />Cancel At Period End</>}
                  </CardButton>
                ) : (
                  <CardButton disabled $owned>
                    <i className="fas fa-check" />Props Add-On Active
                  </CardButton>
                )}
              </>
            ) : (
              <CardButton
                onClick={() => startCheckout({ purchaseType: 'props_addon' })}
                disabled={!access.paid}
                title={!access.paid ? 'An active monthly plan is required before adding Props.' : ''}
              >
                {access.paid ? 'Add Props Access' : 'Monthly Plan Required First'}
              </CardButton>
            )}
          </UpgradeCard>
        </Grid>
      </Section>

      <Section $spaced>
        <SectionTitle>One-Time Credit Top-Ups</SectionTitle>
        <Grid>
          <UpgradeCard>
            <UpgradeCardIcon>
              <i className="fas fa-bolt" />
            </UpgradeCardIcon>
            <UpgradeCardLabel>Custom Credit Top-Up</UpgradeCardLabel>
            <UpgradeCardPrice>
              {pricing?.topUpConfig?.pricePerCredit || 'Price unavailable'}
              <span>per credit</span>
            </UpgradeCardPrice>
            <UpgradeCardFeature>
              <i className="fas fa-check" />
              {`Enter any credit amount in increments of ${Number(pricing?.topUpConfig?.increment || 5)}`}
            </UpgradeCardFeature>
            <UpgradeCardFeature>
              <i className="fas fa-check" />
              {`Minimum ${Number(pricing?.topUpConfig?.minCredits || 5)} credits per top-up`}
            </UpgradeCardFeature>
            <UpgradeCardFeature>
              <i className="fas fa-check" />No subscription changes
            </UpgradeCardFeature>

            <div style={{ marginTop: '0.65rem', display: 'grid', gap: '0.55rem' }}>
              <label htmlFor="top-up-credits" style={{ color: '#cbd5e1', fontSize: '0.8rem', fontWeight: 700 }}>
                Credits to Purchase
              </label>
              <input
                id="top-up-credits"
                type="number"
                min={Number(pricing?.topUpConfig?.minCredits || 5)}
                step={Number(pricing?.topUpConfig?.increment || 5)}
                value={topUpCredits}
                onChange={e => setTopUpCredits(e.target.value)}
                style={{
                  width: '100%',
                  borderRadius: 10,
                  border: '1px solid rgba(148,163,184,.3)',
                  background: 'rgba(15,23,42,.5)',
                  color: '#f8fafc',
                  padding: '0.6rem 0.7rem',
                  fontSize: '0.92rem'
                }}
              />
              <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                {`Estimated total: ${Number.isFinite(Number(topUpCredits)) ? `$${((Number(topUpCredits) * Number(pricing?.topUpConfig?.creditPriceCents || 0)) / 100).toFixed(2)}` : '—'}`}
              </div>
            </div>

            <CardButton onClick={startCustomTopUp}>
              Buy Custom Top-Up
            </CardButton>
          </UpgradeCard>
        </Grid>
      </Section>
    </Main>
  );
};

export default SubscriptionManagement;
